import { useCallback, useRef, useState } from 'react'
import { supabase, edgeFn } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { parseTicketDraft, withTicketDraft, withTicketStatus, ticketSentMessage, type TicketState } from '@/lib/supportTicketCore'

// "Ask Rize" chat state + streaming transport. EPHEMERAL BY DESIGN: state is
// client-side only and reset() wipes it when the panel closes — there is no
// conversations table and no persistence (read-only advisor, Dakri liability
// scope). The ai-assistant edge fn streams SSE JSON events; this is the app's
// first streaming consumer, so it uses raw fetch + getReader (functions.invoke
// buffers the whole body and cannot stream).

export interface AskRizeMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  tools: string[]
  status: 'streaming' | 'done' | 'error'
  /** A support ticket Ask Rize drafted on this turn — the card under the answer. */
  ticket?: TicketState
}

interface StreamEvent {
  type: 'tool' | 'text' | 'replace' | 'ticket_draft' | 'done' | 'error'
  text?: string
  name?: string
  message?: string
  classification?: string
  draft?: unknown
}

let seq = 0
const newId = () => `ask-${Date.now()}-${seq++}`

const GENERIC_ERROR = 'Something went wrong answering that. Try again.'
const TRAINING_ERROR = 'You need to finish the required AI-use training before using Ask Rize. Open Training in the sidebar, complete the modules, then come back.'

export function useAskRize() {
  const { activeOrg } = useAuth()
  const [messages, setMessages] = useState<AskRizeMessage[]>([])
  const [sending, setSending] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  // Wipe the conversation (panel close / org switch). Aborts any in-flight turn.
  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setMessages([])
    setSending(false)
  }, [])

  const send = useCallback(async (question: string) => {
    const q = question.trim()
    if (!q || sending || !activeOrg) return

    const history = messages
      .filter((m) => m.status !== 'error' && m.content.trim())
      .map((m) => ({ role: m.role, content: m.content }))
    const payloadMessages = [...history, { role: 'user' as const, content: q }].slice(-20)

    const userMsg: AskRizeMessage = { id: newId(), role: 'user', content: q, tools: [], status: 'done' }
    const assistantId = newId()
    setMessages((prev) => [...prev, userMsg, { id: assistantId, role: 'assistant', content: '', tools: [], status: 'streaming' }])
    setSending(true)

    const patch = (fn: (m: AskRizeMessage) => AskRizeMessage) =>
      setMessages((prev) => prev.map((m) => (m.id === assistantId ? fn(m) : m)))
    const fail = (text: string) => patch((m) => ({ ...m, content: text, status: 'error' }))

    const ac = new AbortController()
    abortRef.current = ac

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        fail('You are signed out. Sign in again to use Ask Rize.')
        return
      }

      const res = await fetch(edgeFn('ai-assistant'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ organization_id: activeOrg.organization_id, messages: payloadMessages }),
        signal: ac.signal,
      })

      if (!res.ok || !res.body) {
        // Pre-stream failures come back as plain JSON with a status.
        let errBody: { error?: string } = {}
        try { errBody = await res.json() } catch { /* non-JSON error body */ }
        if (errBody.error === 'training_required') fail(TRAINING_ERROR)
        else if (res.status === 429) fail('Slow down a moment — you’ve hit the Ask Rize rate limit. Try again shortly.')
        else fail(errBody.error || GENERIC_ERROR)
        return
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let sawDone = false

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const frames = buffer.split('\n\n')
        buffer = frames.pop() ?? ''
        for (const frame of frames) {
          const line = frame.trim()
          if (!line.startsWith('data: ')) continue
          let evt: StreamEvent
          try { evt = JSON.parse(line.slice(6)) as StreamEvent } catch { continue }

          if (evt.type === 'text' && evt.text) {
            patch((m) => ({ ...m, content: m.content + evt.text }))
          } else if (evt.type === 'tool' && evt.name) {
            patch((m) => (m.tools.includes(evt.name!) ? m : { ...m, tools: [...m.tools, evt.name!] }))
          } else if (evt.type === 'replace' && typeof evt.text === 'string') {
            patch((m) => ({ ...m, content: evt.text! }))
          } else if (evt.type === 'ticket_draft') {
            const draft = parseTicketDraft(evt.draft)
            if (draft) {
              setMessages((prev) => withTicketDraft(prev, assistantId, draft))
              // The server sends no tool chip for the draft; it arrives here, with its card.
              patch((m) => (m.tools.includes('draft_support_ticket') ? m : { ...m, tools: [...m.tools, 'draft_support_ticket'] }))
            }
          } else if (evt.type === 'done') {
            sawDone = true
            patch((m) => ({ ...m, status: 'done' }))
          } else if (evt.type === 'error') {
            sawDone = true
            patch((m) => (m.content.trim()
              ? { ...m, status: 'error' }
              : { ...m, content: evt.message || GENERIC_ERROR, status: 'error' }))
          }
        }
      }

      if (!sawDone) {
        // Stream ended without a terminal event (network drop mid-answer).
        patch((m) => (m.content.trim() ? { ...m, status: 'done' } : { ...m, content: GENERIC_ERROR, status: 'error' }))
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      console.error('useAskRize send failed:', err)
      fail(GENERIC_ERROR)
    } finally {
      if (abortRef.current === ac) abortRef.current = null
      setSending(false)
    }
  }, [activeOrg, messages, sending])

  // The person sent the drafted ticket: close its card and add the confirmation as an
  // assistant turn, so it reads naturally AND rides in the history Ask Rize sees next.
  // A no-op when the conversation was reset meanwhile (panel closed mid-send): the
  // ticket is filed, but nothing is written into a conversation that no longer exists.
  const ticketSent = useCallback((messageId: string, reference: string) => {
    setMessages((prev) => {
      if (!prev.some((m) => m.id === messageId)) return prev
      const confirmation: AskRizeMessage = { id: newId(), role: 'assistant', content: ticketSentMessage(reference), tools: [], status: 'done' }
      return [...withTicketStatus(prev, messageId, 'sent'), confirmation]
    })
  }, [])

  const dismissTicket = useCallback((messageId: string) => {
    setMessages((prev) => withTicketStatus(prev, messageId, 'dismissed'))
  }, [])

  return { messages, sending, send, reset, ticketSent, dismissTicket }
}
